import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TransactionManagement = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [activeTab, setActiveTab] = useState('pending');
  const [transactions, setTransactions] = useState([]);
  const [pendingData, setPendingData] = useState({ pending_response: [], pending_signature: [] });
  const [institutions, setInstitutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showNewTransaction, setShowNewTransaction] = useState(false);
  const [showInstitutionModal, setShowInstitutionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  
  // Form states
  const [transactionType, setTransactionType] = useState('loan');
  const [transactionForm, setTransactionForm] = useState({
    counterparty_type: 'internal',
    counterparty_email: '',
    counterparty_name: '',
    counterparty_institution: '',
    counterparty_wallet: '',
    principal_amount: '',
    collateral_amount: '',
    sale_amount: '',
    interest_rate: '8',
    repo_rate: '6',
    duration_days: '30',
    purpose: '',
    special_terms: '',
    margin_call_threshold: '0.8'
  });
  
  const [institutionForm, setInstitutionForm] = useState({
    name: '',
    institution_type: 'bank',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    lei_code: '',
    wallet_address: ''
  });
  
  const [responseForm, setResponseForm] = useState({
    action: 'accept',
    rejection_reason: ''
  });

  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      
      // Fetch pending transactions
      const pendingRes = await fetch(`${API}/api/securities/transactions/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const pendingData = await pendingRes.json();
      setPendingData(pendingData);
      
      // Fetch all transactions
      const txnRes = await fetch(`${API}/api/securities/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const txnData = await txnRes.json();
      setTransactions(txnData.transactions || []);
      
      // Fetch institutions
      const instRes = await fetch(`${API}/api/securities/institutions`);
      const instData = await instRes.json();
      setInstitutions(instData.institutions || []);
      
      // Fetch stats if admin
      if (isAdmin) {
        const statsRes = await fetch(`${API}/api/securities/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitTransaction = async (e) => {
    e.preventDefault();
    
    const endpoint = transactionType === 'loan' 
      ? '/api/securities/transactions/loan/initiate'
      : '/api/securities/transactions/repo/initiate';
    
    const payload = transactionType === 'loan' ? {
      transaction_type: 'loan',
      counterparty_type: transactionForm.counterparty_type,
      counterparty_email: transactionForm.counterparty_email,
      counterparty_name: transactionForm.counterparty_name,
      counterparty_institution: transactionForm.counterparty_institution || null,
      counterparty_wallet: transactionForm.counterparty_wallet || null,
      security_type: 'BETH',
      principal_amount: parseFloat(transactionForm.principal_amount),
      collateral_amount: parseFloat(transactionForm.collateral_amount),
      collateral_type: 'BETH',
      interest_rate: parseFloat(transactionForm.interest_rate),
      duration_days: parseInt(transactionForm.duration_days),
      purpose: transactionForm.purpose,
      special_terms: transactionForm.special_terms,
      margin_call_threshold: parseFloat(transactionForm.margin_call_threshold)
    } : {
      transaction_type: 'repo',
      counterparty_type: transactionForm.counterparty_type,
      counterparty_email: transactionForm.counterparty_email,
      counterparty_name: transactionForm.counterparty_name,
      counterparty_institution: transactionForm.counterparty_institution || null,
      counterparty_wallet: transactionForm.counterparty_wallet || null,
      security_type: 'BETH',
      sale_amount: parseFloat(transactionForm.sale_amount),
      repo_rate: parseFloat(transactionForm.repo_rate),
      duration_days: parseInt(transactionForm.duration_days),
      purpose: transactionForm.purpose,
      special_terms: transactionForm.special_terms
    };
    
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (data.success) {
        alert(`Transaction created: ${data.transaction_id}\nNotification sent to ${transactionForm.counterparty_email}`);
        setShowNewTransaction(false);
        resetForm();
        fetchData();
      } else {
        alert(data.detail || 'Failed to create transaction');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error creating transaction');
    }
  };

  const submitResponse = async () => {
    if (!selectedTransaction) return;
    
    try {
      const res = await fetch(`${API}/api/securities/transactions/${selectedTransaction.transaction_id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(responseForm)
      });
      
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        setShowResponseModal(false);
        setSelectedTransaction(null);
        fetchData();
      } else {
        alert(data.detail || 'Failed to respond');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // DocuSign signing states
  const [showDocuSignModal, setShowDocuSignModal] = useState(false);
  const [docuSignUrl, setDocuSignUrl] = useState(null);
  const [docuSignLoading, setDocuSignLoading] = useState(false);

  const signTransaction = async () => {
    if (!selectedTransaction) return;
    
    // Check if DocuSign envelope exists
    if (selectedTransaction.docusign?.envelope_id) {
      // Use DocuSign for signing
      await openDocuSignSigning(selectedTransaction);
    } else {
      // Try to create DocuSign envelope first
      await createDocuSignEnvelope(selectedTransaction);
    }
  };

  const createDocuSignEnvelope = async (txn) => {
    setDocuSignLoading(true);
    try {
      const res = await fetch(`${API}/api/docusign/envelope/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ transaction_id: txn.transaction_id })
      });
      
      const data = await res.json();
      if (data.success) {
        // Get signing URL for current user
        const signingUrl = data.signing_urls[user?.email];
        if (signingUrl) {
          setDocuSignUrl(signingUrl);
          setShowDocuSignModal(true);
          setShowSignModal(false);
        } else {
          alert('Signing URL not available for your email. The other party may need to sign first.');
        }
      } else {
        // Fall back to internal signing if DocuSign fails
        await internalSign(txn);
      }
    } catch (err) {
      console.error('DocuSign error:', err);
      // Fall back to internal signing
      await internalSign(txn);
    } finally {
      setDocuSignLoading(false);
    }
  };

  const openDocuSignSigning = async (txn) => {
    setDocuSignLoading(true);
    try {
      const res = await fetch(`${API}/api/docusign/envelope/${txn.docusign.envelope_id}/signing-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          envelope_id: txn.docusign.envelope_id,
          signer_email: user?.email 
        })
      });
      
      const data = await res.json();
      if (data.success && data.signing_url) {
        setDocuSignUrl(data.signing_url);
        setShowDocuSignModal(true);
        setShowSignModal(false);
      } else {
        alert(data.detail || 'Failed to get signing URL');
      }
    } catch (err) {
      console.error('Error getting signing URL:', err);
      alert('Failed to open signing interface');
    } finally {
      setDocuSignLoading(false);
    }
  };

  const internalSign = async (txn) => {
    // Internal e-signature fallback
    try {
      const res = await fetch(`${API}/api/securities/transactions/${txn.transaction_id}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          signature_hash: `SIG-${Date.now()}-${user?.email}`,
          ip_address: 'web-client'
        })
      });
      
      const data = await res.json();
      if (data.success) {
        alert(data.fully_signed 
          ? 'Agreement fully executed! Ready for settlement.' 
          : 'Signature recorded. Waiting for counterparty signature.');
        setShowSignModal(false);
        setSelectedTransaction(null);
        fetchData();
      } else {
        alert(data.detail || 'Failed to sign');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleDocuSignComplete = () => {
    setShowDocuSignModal(false);
    setDocuSignUrl(null);
    setSelectedTransaction(null);
    fetchData();
    alert('Signing process completed! Please check the transaction status.');
  };

  const viewAgreement = async (txn) => {
    try {
      const res = await fetch(`${API}/api/securities/transactions/${txn.transaction_id}/agreement`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedTransaction({ ...txn, agreementHtml: data.agreement_html });
      setShowAgreementModal(true);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const submitInstitution = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API}/api/securities/institutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(institutionForm)
      });
      
      const data = await res.json();
      if (data.success) {
        alert('Institution registered successfully');
        setShowInstitutionModal(false);
        setInstitutionForm({
          name: '', institution_type: 'bank', contact_name: '', contact_email: '',
          contact_phone: '', address: '', lei_code: '', wallet_address: ''
        });
        fetchData();
      } else {
        alert(data.detail || 'Failed to register institution');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const resetForm = () => {
    setTransactionForm({
      counterparty_type: 'internal',
      counterparty_email: '', counterparty_name: '', counterparty_institution: '',
      counterparty_wallet: '', principal_amount: '', collateral_amount: '',
      sale_amount: '', interest_rate: '8', repo_rate: '6', duration_days: '30',
      purpose: '', special_terms: '', margin_call_threshold: '0.8'
    });
  };

  const formatNumber = (num) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num || 0);
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';

  const getStatusBadge = (status) => {
    const colors = {
      pending_counterparty: 'bg-yellow-500/20 text-yellow-400',
      accepted: 'bg-blue-500/20 text-blue-400',
      rejected: 'bg-red-500/20 text-red-400',
      counter_offered: 'bg-purple-500/20 text-purple-400',
      pending_signatures: 'bg-orange-500/20 text-orange-400',
      signed: 'bg-green-500/20 text-green-400',
      active: 'bg-emerald-500/20 text-emerald-400',
      completed: 'bg-gray-500/20 text-gray-400',
      cancelled: 'bg-gray-500/20 text-gray-400',
      defaulted: 'bg-red-500/20 text-red-400'
    };
    return `px-2 py-1 rounded text-xs font-medium ${colors[status] || 'bg-gray-500/20 text-gray-400'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <div className="text-xl">Loading Transaction Manager...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white" data-testid="transaction-management-page">
      {/* Header */}
      <header className="bg-[#0d1421] border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">← Back</button>
            <h1 className="text-xl font-semibold">Securities Transaction Management</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowInstitutionModal(true)}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium"
            >
              + Add Institution
            </button>
            <button
              onClick={() => setShowNewTransaction(true)}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm font-medium"
              data-testid="new-transaction-btn"
            >
              + New Transaction
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pending Actions Alert */}
        {pendingData.total_pending > 0 && (
          <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></span>
              <span className="font-medium">You have {pendingData.total_pending} pending action(s)</span>
              <span className="text-sm text-amber-400">
                ({pendingData.pending_response.length} to respond, {pendingData.pending_signature.length} to sign)
              </span>
            </div>
          </div>
        )}

        {/* Stats */}
        {isAdmin && stats && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
              <div className="text-2xl font-bold text-amber-400">{stats.by_status?.pending_counterparty || 0}</div>
              <div className="text-gray-400 text-sm">Pending Response</div>
            </div>
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
              <div className="text-2xl font-bold text-blue-400">{stats.by_status?.accepted || 0}</div>
              <div className="text-gray-400 text-sm">Accepted</div>
            </div>
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
              <div className="text-2xl font-bold text-green-400">{stats.by_status?.signed || 0}</div>
              <div className="text-gray-400 text-sm">Signed</div>
            </div>
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
              <div className="text-2xl font-bold text-cyan-400">{formatNumber(stats.volume?.total_loan_volume)}</div>
              <div className="text-gray-400 text-sm">Loan Volume (BETH)</div>
            </div>
            <div className="bg-[#0d1421] rounded-xl border border-gray-800 p-4">
              <div className="text-2xl font-bold text-purple-400">{stats.institutions_count || 0}</div>
              <div className="text-gray-400 text-sm">Institutions</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-800 pb-4">
          {['pending', 'all', 'sent', 'received', 'institutions'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize ${
                activeTab === tab ? 'bg-amber-600 text-white' : 'bg-[#151d2e] text-gray-400 hover:text-white'
              }`}
            >
              {tab} {tab === 'pending' && pendingData.total_pending > 0 && `(${pendingData.total_pending})`}
            </button>
          ))}
        </div>

        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-6">
            {/* Pending Response */}
            {pendingData.pending_response.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-yellow-400">Awaiting Your Response</h3>
                <div className="space-y-3">
                  {pendingData.pending_response.map(txn => (
                    <div key={txn.transaction_id} className="bg-[#0d1421] rounded-xl border border-yellow-800 p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-sm">{txn.transaction_id}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${txn.transaction_type === 'loan' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                              {txn.transaction_type.toUpperCase()}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-gray-400">
                            From: <span className="text-white">{txn.initiator.name}</span> ({txn.initiator.email})
                          </div>
                          <div className="mt-1 text-sm">
                            Amount: <span className="text-amber-400 font-semibold">
                              {formatNumber(txn.principal_amount || txn.sale_amount)} BETH
                            </span>
                            {txn.transaction_type === 'loan' && (
                              <span className="text-gray-400 ml-2">@ {txn.interest_rate}% APR for {txn.duration_days} days</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewAgreement(txn)}
                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => { setSelectedTransaction(txn); setShowResponseModal(true); }}
                            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 rounded text-sm font-medium"
                          >
                            Respond
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Signature */}
            {pendingData.pending_signature.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-orange-400">Awaiting Your Signature</h3>
                <div className="space-y-3">
                  {pendingData.pending_signature.map(txn => (
                    <div key={txn.transaction_id} className="bg-[#0d1421] rounded-xl border border-orange-800 p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-sm">{txn.transaction_id}</span>
                            <span className={getStatusBadge(txn.status)}>{txn.status.replace(/_/g, ' ')}</span>
                          </div>
                          <div className="mt-2 text-sm">
                            {txn.transaction_type === 'loan' ? 'Loan' : 'Repo'}: {formatNumber(txn.principal_amount || txn.sale_amount)} BETH
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewAgreement(txn)}
                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                          >
                            View Agreement
                          </button>
                          <button
                            onClick={() => { setSelectedTransaction(txn); setShowSignModal(true); }}
                            className="px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded text-sm font-medium"
                          >
                            Sign Agreement
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pendingData.total_pending === 0 && (
              <div className="text-center py-12 text-gray-500">
                No pending actions. You're all caught up!
              </div>
            )}
          </div>
        )}

        {/* All/Sent/Received Tabs */}
        {['all', 'sent', 'received'].includes(activeTab) && (
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#151d2e]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Counterparty</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Created</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions
                  .filter(txn => {
                    if (activeTab === 'sent') return txn.user_role === 'initiator';
                    if (activeTab === 'received') return txn.user_role === 'counterparty';
                    return true;
                  })
                  .map(txn => (
                    <tr key={txn.transaction_id} className="border-t border-gray-800 hover:bg-[#151d2e]">
                      <td className="px-4 py-3 text-sm font-mono">{txn.transaction_id}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${txn.transaction_type === 'loan' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {txn.transaction_type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {txn.user_role === 'initiator' ? txn.counterparty.name : txn.initiator.name}
                        {txn.counterparty.institution && <span className="text-gray-500 text-xs ml-1">({txn.counterparty.institution})</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatNumber(txn.principal_amount || txn.sale_amount)} BETH</td>
                      <td className="px-4 py-3"><span className={getStatusBadge(txn.status)}>{txn.status.replace(/_/g, ' ')}</span></td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(txn.created_at)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => viewAgreement(txn)} className="text-amber-400 hover:text-amber-300 text-sm">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Institutions Tab */}
        {activeTab === 'institutions' && (
          <div className="bg-[#0d1421] rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#151d2e]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Institution</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Contact</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">LEI</th>
                </tr>
              </thead>
              <tbody>
                {institutions.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No institutions registered</td></tr>
                ) : institutions.map(inst => (
                  <tr key={inst.institution_id} className="border-t border-gray-800 hover:bg-[#151d2e]">
                    <td className="px-4 py-3 font-medium">{inst.name}</td>
                    <td className="px-4 py-3 text-sm capitalize">{inst.institution_type}</td>
                    <td className="px-4 py-3 text-sm">{inst.contact_name}<br/><span className="text-gray-500">{inst.contact_email}</span></td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${inst.status === 'verified' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {inst.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-400">{inst.lei_code || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* New Transaction Modal */}
      {showNewTransaction && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1421] rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold">Initiate Securities Transaction</h2>
            </div>
            <form onSubmit={submitTransaction} className="p-6 space-y-6">
              {/* Transaction Type */}
              <div className="flex gap-4">
                <button type="button" onClick={() => setTransactionType('loan')}
                  className={`flex-1 py-3 rounded-lg font-medium ${transactionType === 'loan' ? 'bg-amber-600 text-white' : 'bg-[#151d2e] text-gray-400'}`}>
                  Securities Loan
                </button>
                <button type="button" onClick={() => setTransactionType('repo')}
                  className={`flex-1 py-3 rounded-lg font-medium ${transactionType === 'repo' ? 'bg-blue-600 text-white' : 'bg-[#151d2e] text-gray-400'}`}>
                  Repo Agreement
                </button>
              </div>

              {/* Counterparty Type */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Counterparty Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="counterparty_type" value="internal"
                      checked={transactionForm.counterparty_type === 'internal'}
                      onChange={(e) => setTransactionForm({...transactionForm, counterparty_type: e.target.value})}/>
                    <span>Internal (Platform User)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="counterparty_type" value="external"
                      checked={transactionForm.counterparty_type === 'external'}
                      onChange={(e) => setTransactionForm({...transactionForm, counterparty_type: e.target.value})}/>
                    <span>External (Institution)</span>
                  </label>
                </div>
              </div>

              {/* Counterparty Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Counterparty Email *</label>
                  <input type="email" required value={transactionForm.counterparty_email}
                    onChange={(e) => setTransactionForm({...transactionForm, counterparty_email: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Counterparty Name *</label>
                  <input type="text" required value={transactionForm.counterparty_name}
                    onChange={(e) => setTransactionForm({...transactionForm, counterparty_name: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                </div>
              </div>

              {transactionForm.counterparty_type === 'external' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Institution Name</label>
                    <input type="text" value={transactionForm.counterparty_institution}
                      onChange={(e) => setTransactionForm({...transactionForm, counterparty_institution: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Wallet Address</label>
                    <input type="text" value={transactionForm.counterparty_wallet} placeholder="0x..."
                      onChange={(e) => setTransactionForm({...transactionForm, counterparty_wallet: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                  </div>
                </div>
              )}

              {/* Loan-specific fields */}
              {transactionType === 'loan' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Principal Amount (BETH) *</label>
                    <input type="number" required value={transactionForm.principal_amount}
                      onChange={(e) => setTransactionForm({...transactionForm, principal_amount: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Collateral Amount (BETH) *</label>
                    <input type="number" required value={transactionForm.collateral_amount}
                      onChange={(e) => setTransactionForm({...transactionForm, collateral_amount: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Interest Rate (% APR)</label>
                    <input type="number" step="0.1" value={transactionForm.interest_rate}
                      onChange={(e) => setTransactionForm({...transactionForm, interest_rate: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Duration (Days)</label>
                    <select value={transactionForm.duration_days}
                      onChange={(e) => setTransactionForm({...transactionForm, duration_days: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white">
                      <option value="7">7 Days</option>
                      <option value="14">14 Days</option>
                      <option value="30">30 Days</option>
                      <option value="60">60 Days</option>
                      <option value="90">90 Days</option>
                      <option value="180">180 Days</option>
                      <option value="365">365 Days</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Repo-specific fields */}
              {transactionType === 'repo' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Sale Amount (BETH) *</label>
                    <input type="number" required value={transactionForm.sale_amount}
                      onChange={(e) => setTransactionForm({...transactionForm, sale_amount: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Repo Rate (% APR)</label>
                    <input type="number" step="0.1" value={transactionForm.repo_rate}
                      onChange={(e) => setTransactionForm({...transactionForm, repo_rate: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Duration (Days)</label>
                    <select value={transactionForm.duration_days}
                      onChange={(e) => setTransactionForm({...transactionForm, duration_days: e.target.value})}
                      className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white">
                      <option value="1">Overnight (1 Day)</option>
                      <option value="7">1 Week</option>
                      <option value="14">2 Weeks</option>
                      <option value="30">1 Month</option>
                      <option value="60">2 Months</option>
                      <option value="90">3 Months</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Purpose and Terms */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Purpose</label>
                <input type="text" value={transactionForm.purpose}
                  onChange={(e) => setTransactionForm({...transactionForm, purpose: e.target.value})}
                  className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"
                  placeholder="e.g., Short-term liquidity, Working capital"/>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Special Terms (Optional)</label>
                <textarea value={transactionForm.special_terms}
                  onChange={(e) => setTransactionForm({...transactionForm, special_terms: e.target.value})}
                  className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white" rows={2}/>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => { setShowNewTransaction(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-800">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg font-medium">
                  Send Transaction Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1421] rounded-xl border border-gray-700 w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">Respond to Transaction</h2>
            <div className="bg-[#151d2e] rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-400">Transaction: <span className="text-white font-mono">{selectedTransaction.transaction_id}</span></p>
              <p className="text-sm text-gray-400 mt-1">Amount: <span className="text-amber-400 font-semibold">{formatNumber(selectedTransaction.principal_amount || selectedTransaction.sale_amount)} BETH</span></p>
            </div>
            
            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-700 cursor-pointer hover:bg-[#151d2e]">
                <input type="radio" name="action" value="accept" checked={responseForm.action === 'accept'}
                  onChange={(e) => setResponseForm({...responseForm, action: e.target.value})}/>
                <div>
                  <div className="font-medium text-green-400">Accept</div>
                  <div className="text-sm text-gray-400">Accept the transaction and proceed to signing</div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-700 cursor-pointer hover:bg-[#151d2e]">
                <input type="radio" name="action" value="reject" checked={responseForm.action === 'reject'}
                  onChange={(e) => setResponseForm({...responseForm, action: e.target.value})}/>
                <div>
                  <div className="font-medium text-red-400">Reject</div>
                  <div className="text-sm text-gray-400">Decline this transaction</div>
                </div>
              </label>
            </div>
            
            {responseForm.action === 'reject' && (
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-1">Reason for Rejection</label>
                <textarea value={responseForm.rejection_reason}
                  onChange={(e) => setResponseForm({...responseForm, rejection_reason: e.target.value})}
                  className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white" rows={2}/>
              </div>
            )}
            
            <div className="flex gap-3">
              <button onClick={() => { setShowResponseModal(false); setSelectedTransaction(null); }}
                className="flex-1 px-4 py-2 border border-gray-600 rounded-lg text-gray-300">Cancel</button>
              <button onClick={submitResponse}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${responseForm.action === 'accept' ? 'bg-green-600 hover:bg-green-500' : 'bg-red-600 hover:bg-red-500'}`}>
                {responseForm.action === 'accept' ? 'Accept' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Modal */}
      {showSignModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1421] rounded-xl border border-gray-700 w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">Sign Agreement</h2>
            <div className="bg-[#151d2e] rounded-lg p-4 mb-4">
              <p className="text-sm">By signing this agreement, you confirm that you have read, understood, and agree to all terms and conditions of this {selectedTransaction.transaction_type} transaction.</p>
            </div>
            
            {/* DocuSign badge */}
            <div className="flex items-center gap-2 mb-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
              <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-400">DocuSign E-Signature</p>
                <p className="text-xs text-gray-400">Legally binding electronic signature</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-400 mb-4">
              You will be redirected to DocuSign to complete your legally binding electronic signature.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setShowSignModal(false); setSelectedTransaction(null); }}
                className="flex-1 px-4 py-2 border border-gray-600 rounded-lg text-gray-300">Cancel</button>
              <button 
                onClick={signTransaction}
                disabled={docuSignLoading}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {docuSignLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Loading...
                  </>
                ) : (
                  'Sign with DocuSign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agreement Modal */}
      {showAgreementModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="bg-[#0d1421] px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Agreement Document</h2>
              <button onClick={() => { setShowAgreementModal(false); setSelectedTransaction(null); }}
                className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <div dangerouslySetInnerHTML={{ __html: selectedTransaction.agreementHtml }} />
            </div>
          </div>
        </div>
      )}

      {/* Institution Modal */}
      {showInstitutionModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1421] rounded-xl border border-gray-700 w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Register External Institution</h2>
            <form onSubmit={submitInstitution} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Institution Name *</label>
                <input type="text" required value={institutionForm.name}
                  onChange={(e) => setInstitutionForm({...institutionForm, name: e.target.value})}
                  className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Institution Type</label>
                <select value={institutionForm.institution_type}
                  onChange={(e) => setInstitutionForm({...institutionForm, institution_type: e.target.value})}
                  className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white">
                  <option value="bank">Bank</option>
                  <option value="fund">Investment Fund</option>
                  <option value="broker-dealer">Broker-Dealer</option>
                  <option value="family_office">Family Office</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Name *</label>
                  <input type="text" required value={institutionForm.contact_name}
                    onChange={(e) => setInstitutionForm({...institutionForm, contact_name: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Email *</label>
                  <input type="email" required value={institutionForm.contact_email}
                    onChange={(e) => setInstitutionForm({...institutionForm, contact_email: e.target.value})}
                    className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">LEI Code (Optional)</label>
                <input type="text" value={institutionForm.lei_code} placeholder="20-character Legal Entity Identifier"
                  onChange={(e) => setInstitutionForm({...institutionForm, lei_code: e.target.value})}
                  className="w-full bg-[#151d2e] border border-gray-700 rounded-lg px-4 py-2 text-white"/>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowInstitutionModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-600 rounded-lg text-gray-300">Cancel</button>
                <button type="submit"
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg font-medium">
                  Register Institution
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DocuSign Signing Modal */}
      {showDocuSignModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-[#0d1421] px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-white">DocuSign E-Signature</h2>
                <p className="text-sm text-gray-400">Complete your legally binding signature below</p>
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={handleDocuSignComplete}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white text-sm font-medium"
                >
                  Done Signing
                </button>
                <button 
                  onClick={() => { setShowDocuSignModal(false); setDocuSignUrl(null); }}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              {docuSignUrl ? (
                <iframe
                  src={docuSignUrl}
                  className="w-full h-full border-0"
                  title="DocuSign Signing"
                  allow="camera; microphone"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading DocuSign...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionManagement;
